// ============================================================================
// 🏗️ MANPOWER ALLOCATION SERVICE
// Path: services/manpower.service.ts
// 
// Handles all Firestore operations for manpower allocation management
// ✅ FIXED: PROJECT CODE import issue (line 420)
// ============================================================================

import { 
  collection, 
  doc, 
  query, 
  orderBy, 
  getDocs,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  Timestamp,
  Unsubscribe
} from 'firebase/firestore';
import { db } from './firebase';

// ============================================================================
// INTERFACES
// ============================================================================

export interface ManpowerEmployee {
  sNo: number;
  name: string;
  empCode: string;           // From Excel: 2753
  empNumber: string;         // Matched from MasterSheet: EMP2753
  site: string;
  position: string;
  projectCode: string;
  projectName?: string;      // ✅ FIXED: Non-optional, always present (can be empty string)
  designation?: string;
  department?: string;
  avatar?: string;
  nationality?: string;
  joiningDate?: string;
  addedAt: Timestamp;
  addedBy: string;
}

export interface ManpowerProject {
  id: string;
  projectName: string;
  projectCode?: string;
  location?: string;
  totalEmployees: number;
  employees: ManpowerEmployee[];
  createdAt: Timestamp;
  updatedAt: Timestamp;
  createdBy: string;
  lastModifiedBy: string;
  isActive: boolean;
}

export interface EmployeeMasterData {
  employeeNumber: string;    // EMP2753
  name: string;
  designation?: string;
  department?: string;
  nationality?: string;
  joiningDate?: string;
  email?: string;
  phone?: string;
}

export interface ExcelImportRow {
  'S.NO'?: string | number;
  'SNO'?: string | number;
  'S NO'?: string | number;
  'NAME'?: string;
  'EMP. CODE'?: string | number;
  'EMP.CODE'?: string | number;
  'EMP CODE'?: string | number;
  'EMPCODE'?: string | number;
  'SITE'?: string;
  'POSITION'?: string;
  'PROJECT CODE'?: string;
  'PROJECT.CODE'?: string;
  'PROJECTCODE'?: string;
  [key: string]: any; // Allow any other columns
}

export interface ImportResult {
  success: boolean;
  totalRows: number;
  successfulImports: number;
  failedImports: number;
  matchedEmployees: number;
  unmatchedEmployees: number;
  errors: string[];
  unmatchedCodes: string[];
}

// ============================================================================
// MANPOWER SERVICE CLASS
// ============================================================================

class ManpowerService {
  private readonly COLLECTION_PATH = 'Manpower Allocation';
  private readonly MASTERSHEET_PATH = 'MasterSheet/Employee-Data/employees';
  
  // ========================================================================
  // EMPLOYEE CODE MAPPING
  // ========================================================================
  
  /**
   * Convert Excel employee code (2753) to MasterSheet format (EMP2753)
   */
  private formatEmployeeNumber(code: string | number): string {
    const cleanCode = code.toString().replace(/^EMP/i, '').trim();
    return `EMP${cleanCode}`;
  }



  /**
   * Extract clean code from employee number (EMP2753 → 2753)
   */
  private extractEmployeeCode(empNumber: string): string {
    return empNumber.replace(/^EMP/i, '');
  }

  // ========================================================================
  // MASTERSHEET DATA FETCHING
  // ========================================================================
  
  /**
   * Fetch employee data from MasterSheet by employee code
   */
  async getEmployeeFromMasterSheet(empCode: string): Promise<EmployeeMasterData | null> {
    try {
      const empNumber = this.formatEmployeeNumber(empCode);
      const empDoc = doc(db, this.MASTERSHEET_PATH, empNumber);
      const snapshot = await getDoc(empDoc);
      
      if (!snapshot.exists()) {
        console.warn(`Employee ${empNumber} not found in MasterSheet`);
        return null;
      }

      const data = snapshot.data();
      return {
        employeeNumber: empNumber,
        name: data.name || data.employeeName || 'Unknown',
        designation: data.designation || '',
        department: data.department || '',
        nationality: data.nationality || '',
        joiningDate: data.joiningDate || data.dateOfJoining || '',
        email: data.email || '',
        phone: data.phone || data.phoneNumber || ''
      };
    } catch (error) {
      console.error(`Error fetching employee ${empCode}:`, error);
      return null;
    }
  }

  /**
   * Batch fetch multiple employees from MasterSheet
   */
  async batchGetEmployeesFromMasterSheet(empCodes: string[]): Promise<Map<string, EmployeeMasterData>> {
    const employeeMap = new Map<string, EmployeeMasterData>();
    
    try {
      const fetchPromises = empCodes.map(code => this.getEmployeeFromMasterSheet(code));
      const results = await Promise.all(fetchPromises);
      
      results.forEach((employee, index) => {
        if (employee) {
          employeeMap.set(empCodes[index], employee);
        }
      });
      
      console.log(`✅ Matched ${employeeMap.size}/${empCodes.length} employees from MasterSheet`);
    } catch (error) {
      console.error('Error batch fetching employees:', error);
    }
    
    return employeeMap;
  }

  /**
   * Get ALL employees from MasterSheet
   */
  async getAllEmployeesFromMasterSheet(): Promise<EmployeeMasterData[]> {
    try {
      const employeesRef = collection(db, this.MASTERSHEET_PATH);
      const snapshot = await getDocs(employeesRef);
      
      const employees: EmployeeMasterData[] = [];
      snapshot.forEach(doc => {
        const data = doc.data();
        employees.push({
          employeeNumber: doc.id, // Document ID is the employee number (EMP2753)
          name: data.name || data.employeeName || 'Unknown',
          designation: data.designation || '',
          department: data.department || '',
          nationality: data.nationality || '',
          joiningDate: data.joiningDate || data.dateOfJoining || '',
          email: data.email || '',
          phone: data.phone || data.phoneNumber || ''
        });
      });
      
      console.log(`✅ Fetched ${employees.length} employees from MasterSheet`);
      return employees;
    } catch (error) {
      console.error('Error fetching all employees from MasterSheet:', error);
      return [];
    }
  }

  // ========================================================================
  // PROJECT MANAGEMENT
  // ========================================================================
  
  /**
   * Get all manpower allocation projects
   */
  async getAllProjects(): Promise<ManpowerProject[]> {
    try {
      const projectsRef = collection(db, this.COLLECTION_PATH);
      const q = query(projectsRef, orderBy('updatedAt', 'desc'));
      const snapshot = await getDocs(q);
      
      const projects: ManpowerProject[] = [];
      
      snapshot.forEach(doc => {
        const data = doc.data();
        
        // 🔍 DEBUG: Log raw Firestore data
        if (doc.id === 'Adil Khurshid' || projects.length === 0) {
          console.log('🔍 DEBUG: Loading project:', doc.id);
          if (data.employees && data.employees.length > 0) {
            console.log('🔍 First employee RAW from Firestore:', data.employees[0]);
            console.log('🔍 First employee projectCode from Firestore:', data.employees[0].projectCode);
          }
        }
        
        // ✅ CRITICAL FIX: Ensure ALL employees have projectCode field (non-optional)
        const employees = (data.employees || []).map((emp: any) => ({
          ...emp,
          projectCode: emp.projectCode !== undefined && emp.projectCode !== null ? emp.projectCode : ''
        }));
        
        projects.push({
          id: doc.id,
          projectName: doc.id,
          projectCode: data.projectCode || '',
          location: data.location || '',
          totalEmployees: employees.length,
          employees: employees,
          createdAt: data.createdAt || Timestamp.now(),
          updatedAt: data.updatedAt || Timestamp.now(),
          createdBy: data.createdBy || 'Unknown',
          lastModifiedBy: data.lastModifiedBy || 'Unknown',
          isActive: data.isActive !== false
        });
      });
      
      console.log(`📊 Loaded ${projects.length} projects`);
      return projects;
    } catch (error) {
      console.error('Error fetching projects:', error);
      throw new Error('Failed to fetch manpower projects');
    }
  }

  /**
   * Get specific project by name
   */
  async getProject(projectName: string): Promise<ManpowerProject | null> {
    try {
      const projectDoc = doc(db, this.COLLECTION_PATH, projectName);
      const snapshot = await getDoc(projectDoc);
      
      if (!snapshot.exists()) {
        return null;
      }

      const data = snapshot.data();
      
      // ✅ CRITICAL FIX: Ensure ALL employees have projectCode field (non-optional)
      const employees = (data.employees || []).map((emp: any) => ({
        ...emp,
        projectCode: emp.projectCode !== undefined && emp.projectCode !== null ? emp.projectCode : ''
      }));
      
      return {
        id: snapshot.id,
        projectName: snapshot.id,
        projectCode: data.projectCode || '',
        location: data.location || '',
        totalEmployees: employees.length,
        employees: employees,
        createdAt: data.createdAt || Timestamp.now(),
        updatedAt: data.updatedAt || Timestamp.now(),
        createdBy: data.createdBy || 'Unknown',
        lastModifiedBy: data.lastModifiedBy || 'Unknown',
        isActive: data.isActive !== false
      };
    } catch (error) {
      console.error(`Error fetching project ${projectName}:`, error);
      throw new Error('Failed to fetch project');
    }
  }

  /**
   * Create new project
   */
  async createProject(
    projectName: string, 
    metadata: {
      projectCode?: string;
      location?: string;
      createdBy: string;
    }
  ): Promise<void> {
    try {
      const projectDoc = doc(db, this.COLLECTION_PATH, projectName);
      
      await setDoc(projectDoc, {
        projectCode: metadata.projectCode || '',
        location: metadata.location || '',
        employees: [],
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        createdBy: metadata.createdBy,
        lastModifiedBy: metadata.createdBy,
        isActive: true
      });
      
      console.log(`✅ Created project: ${projectName}`);
    } catch (error) {
      console.error('Error creating project:', error);
      throw new Error('Failed to create project');
    }
  }

  /**
   * Delete project
   */
  async deleteProject(projectName: string): Promise<void> {
    try {
      const projectDoc = doc(db, this.COLLECTION_PATH, projectName);
      await deleteDoc(projectDoc);
      console.log(`🗑️ Deleted project: ${projectName}`);
    } catch (error) {
      console.error('Error deleting project:', error);
      throw new Error('Failed to delete project');
    }
  }

  // ========================================================================
  // EMPLOYEE MANAGEMENT WITHIN PROJECT
  // ========================================================================
  
  /**
   * Add single employee to project
   */
  async addEmployeeToProject(
    projectName: string, 
    employee: ManpowerEmployee,
    userId: string
  ): Promise<void> {
    try {
      const projectDoc = doc(db, this.COLLECTION_PATH, projectName);
      const snapshot = await getDoc(projectDoc);
      
      if (!snapshot.exists()) {
        throw new Error(`Project ${projectName} does not exist`);
      }

      const data = snapshot.data();
      const employees = data.employees || [];
      
      // Check for duplicate
      const exists = employees.some((emp: ManpowerEmployee) => 
        emp.empCode === employee.empCode || emp.empNumber === employee.empNumber
      );
      
      if (exists) {
        throw new Error(`Employee ${employee.empCode} already exists in ${projectName}`);
      }

      employees.push({
        ...employee,
        addedAt: Timestamp.now(),
        addedBy: userId
      });

      await updateDoc(projectDoc, {
        employees,
        updatedAt: Timestamp.now(),
        lastModifiedBy: userId
      });
      
      console.log(`✅ Added employee ${employee.empCode} to ${projectName}`);
    } catch (error) {
      console.error('Error adding employee:', error);
      throw error;
    }
  }

  /**
   * Remove employee from project
   */
  async removeEmployeeFromProject(
    projectName: string,
    empCode: string,
    userId: string
  ): Promise<void> {
    try {
      const projectDoc = doc(db, this.COLLECTION_PATH, projectName);
      const snapshot = await getDoc(projectDoc);
      
      if (!snapshot.exists()) {
        throw new Error(`Project ${projectName} does not exist`);
      }

      const data = snapshot.data();
      const employees = data.employees || [];
      
      const filteredEmployees = employees.filter((emp: ManpowerEmployee) => 
        emp.empCode !== empCode && emp.empNumber !== this.formatEmployeeNumber(empCode)
      );

      await updateDoc(projectDoc, {
        employees: filteredEmployees,
        updatedAt: Timestamp.now(),
        lastModifiedBy: userId
      });
      
      console.log(`🗑️ Removed employee ${empCode} from ${projectName}`);
    } catch (error) {
      console.error('Error removing employee:', error);
      throw error;
    }
  }

  /**
   * Update employee in project
   */
  async updateEmployeeInProject(
    projectName: string,
    empCode: string,
    updates: Partial<ManpowerEmployee>,
    userId: string
  ): Promise<void> {
    try {
      const projectDoc = doc(db, this.COLLECTION_PATH, projectName);
      const snapshot = await getDoc(projectDoc);
      
      if (!snapshot.exists()) {
        throw new Error(`Project ${projectName} does not exist`);
      }

      const data = snapshot.data();
      const employees = data.employees || [];
      
      const updatedEmployees = employees.map((emp: ManpowerEmployee) => {
        if (emp.empCode === empCode || emp.empNumber === this.formatEmployeeNumber(empCode)) {
          return { ...emp, ...updates };
        }
        return emp;
      });

      await updateDoc(projectDoc, {
        employees: updatedEmployees,
        updatedAt: Timestamp.now(),
        lastModifiedBy: userId
      });
      
      console.log(`✏️ Updated employee ${empCode} in ${projectName}`);
    } catch (error) {
      console.error('Error updating employee:', error);
      throw error;
    }
  }

  // ========================================================================
  // EXCEL IMPORT
  // ========================================================================
  
  /**
   * Import employees from Excel data
   */
  async importEmployeesFromExcel(
    projectName: string,
    excelData: ExcelImportRow[],
    userId: string,
    replaceExisting: boolean = false
  ): Promise<ImportResult> {
    const result: ImportResult = {
      success: false,
      totalRows: excelData.length,
      successfulImports: 0,
      failedImports: 0,
      matchedEmployees: 0,
      unmatchedEmployees: 0,
      errors: [],
      unmatchedCodes: []
    };

    try {
      console.log(`📥 Starting import of ${excelData.length} employees to ${projectName}`);
      
      // Debug: Show actual columns in Excel file
      if (excelData.length > 0) {
        const firstRow = excelData[0];
        const columns = Object.keys(firstRow);
        console.log(`📋 Excel columns found:`, columns);
        console.log(`📋 First row sample:`, firstRow);
        console.log(`📋 PROJECT CODE column value:`, firstRow['PROJECT CODE']);
      }
      
      // Filter out empty rows and extract valid employee codes
      const validRows: { row: ExcelImportRow; index: number }[] = [];
      const empCodes: string[] = [];
      
      excelData.forEach((row, index) => {
        // Check if row has EMP. CODE
        const empCodeRaw = row['EMP. CODE'] || row['EMP.CODE'] || row['EMP CODE'] || row['EMPCODE'];
        
        if (!empCodeRaw || empCodeRaw === null || empCodeRaw === undefined) {
          result.errors.push(`Row ${index + 1}: Missing EMP. CODE`);
          result.failedImports++;
          return;
        }
        
        const empCode = empCodeRaw.toString().trim();
        
        if (!empCode || empCode === '' || empCode === '-') {
          result.errors.push(`Row ${index + 1}: Empty EMP. CODE`);
          result.failedImports++;
          return;
        }
        
        validRows.push({ row, index });
        empCodes.push(empCode);
      });
      
      console.log(`✅ Found ${validRows.length} valid rows out of ${excelData.length} total rows`);
      
      if (validRows.length === 0) {
        result.errors.push('No valid rows found in Excel file');
        return result;
      }
      
      // Batch fetch from MasterSheet
      const masterSheetData = await this.batchGetEmployeesFromMasterSheet(empCodes);
      
      // Build employee objects
      const employees: ManpowerEmployee[] = [];
      
      validRows.forEach(({ row, index }) => {
        try {
          // Get employee code with fallback column names
          const empCodeRaw = row['EMP. CODE'] || row['EMP.CODE'] || row['EMP CODE'] || row['EMPCODE'];
          
          // TypeScript null check - this should never happen since we validated in validRows
          if (!empCodeRaw) {
            result.failedImports++;
            result.errors.push(`Row ${index + 1}: Missing employee code`);
            return;
          }
          
          const empCode = empCodeRaw.toString().trim();
          
          const masterData = masterSheetData.get(empCode);
          
          if (!masterData) {
            result.unmatchedEmployees++;
            result.unmatchedCodes.push(empCode);
            result.errors.push(`Row ${index + 1}: Employee ${empCode} not found in MasterSheet`);
            return;
          }

          result.matchedEmployees++;

          // Safely get values with fallbacks
          const sNoRaw = row['S.NO'] || row['SNO'] || row['S NO'] || (index + 1);
          const sNo = typeof sNoRaw === 'number' ? sNoRaw : parseInt(sNoRaw.toString()) || (index + 1);
          
          const nameRaw = row['NAME'] || '';
          const name = masterData.name || (nameRaw ? nameRaw.toString().trim() : 'Unknown');
          
          const siteRaw = row['SITE'] || '';
          const site = siteRaw ? siteRaw.toString().trim() : 'Unknown Site';
          
          const positionRaw = row['POSITION'] || '';
          const position = positionRaw ? positionRaw.toString().trim() : 'Unknown Position';
          
          // ✅ FIXED: Extract project code - USE EMPTY STRING INSTEAD OF UNDEFINED
          const projectCodeRaw = 
  row['PROJECT CODE'] || 
  row['PROJECT.CODE'] || 
  row['PROJECTCODE'] || 
  row['Project Code'] ||
  row['project code'] ||
  '';

            
            let projectCode = projectCodeRaw ? projectCodeRaw.toString().trim().toUpperCase() : '';

            // Option 1: Remove "PJ" prefix if it exists
            projectCode = projectCode.replace(/^PJ/i, '');

          // 🔍 DEBUG: Log projectCode extraction
          if (index === 0) {
            console.log('🔍 DEBUG Row 1 projectCode extraction:', {
              projectCodeRaw,
              projectCode,
              isEmpty: projectCode === '',
              allRowKeys: Object.keys(row)
            });
          }

          const employee: ManpowerEmployee = {
            sNo: sNo,
            name: name,
            empCode: empCode,
            empNumber: masterData.employeeNumber,
            site: site,
            position: position,
            projectCode: projectCode, // ✅ Now non-optional, always present
            designation: masterData.designation,
            department: masterData.department,
            nationality: masterData.nationality,
            joiningDate: masterData.joiningDate,
            addedAt: Timestamp.now(),
            addedBy: userId
          };

          // 🔍 DEBUG: Verify employee object has projectCode
          if (index === 0) {
            console.log('🔍 DEBUG Row 1 employee object:', {
              empCode: employee.empCode,
              projectCode: employee.projectCode,
              hasProjectCode: 'projectCode' in employee,
              projectCodeType: typeof employee.projectCode
            });
          }

          employees.push(employee);
          result.successfulImports++;
        } catch (error) {
          result.failedImports++;
          result.errors.push(`Row ${index + 1}: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
      });

      if (employees.length === 0) {
        result.success = false;
        result.errors.push('No valid employees to import');
        return result;
      }

      // Clean employees - FORCE projectCode to be included even if empty
      const cleanedEmployees = employees.map(emp => {
        const cleaned: any = {
          // Always include projectCode, even if empty
          projectCode: emp.projectCode || ''
        };
        
        Object.keys(emp).forEach(key => {
          const value = (emp as any)[key];
          if (value !== undefined) {
            cleaned[key] = value;
          }
        });
        
        return cleaned as ManpowerEmployee;
      });

      console.log('🔍 DEBUG: About to save employees to Firestore');
      console.log('🔍 replaceExisting flag:', replaceExisting);
      console.log('🔍 Total employees to save:', cleanedEmployees.length);
      if (cleanedEmployees.length > 0) {
        console.log('🔍 First employee CLEANED:', cleanedEmployees[0]);
        console.log('🔍 First employee projectCode AFTER CLEANING:', cleanedEmployees[0].projectCode);
        console.log('🔍 projectCode is included AFTER CLEANING?:', 'projectCode' in cleanedEmployees[0]);
        console.log('🔍 projectCode type AFTER CLEANING:', typeof cleanedEmployees[0].projectCode);
      }

      // Save to Firestore
      const projectDoc = doc(db, this.COLLECTION_PATH, projectName);
      
      if (replaceExisting) {
        // Get existing document to preserve other fields
        const snapshot = await getDoc(projectDoc);
        const existingData = snapshot.exists() ? snapshot.data() : {};
        
        // Replace all employees - keep other fields like location, projectCode (root)
        await setDoc(projectDoc, {
          ...existingData, // Keep existing fields
          employees: cleanedEmployees, // REPLACE employees array completely
          updatedAt: Timestamp.now(),
          lastModifiedBy: userId
        });
        
        console.log('✅ REPLACED all employees in Firestore (preserved other fields)');
        console.log('🔍 Save completed at:', new Date().toISOString());
        console.log('🔍 Saved', cleanedEmployees.length, 'employees with projectCode');
      } else {
        console.log('🔍 MERGE mode: Adding new employees to existing ones');
        
        // Merge with existing employees
        const snapshot = await getDoc(projectDoc);
        let existingEmployees: ManpowerEmployee[] = [];
        
        if (snapshot.exists()) {
          existingEmployees = snapshot.data().employees || [];
        }

        // Remove duplicates - keep new imports
        const existingCodes = new Set(existingEmployees.map(e => e.empCode));
        const newEmployees = cleanedEmployees.filter(e => !existingCodes.has(e.empCode));
        const finalEmployees = [...existingEmployees, ...newEmployees];

        await setDoc(projectDoc, {
          employees: finalEmployees,
          updatedAt: Timestamp.now(),
          lastModifiedBy: userId,
          createdAt: snapshot.exists() ? snapshot.data().createdAt : Timestamp.now(),
          createdBy: snapshot.exists() ? snapshot.data().createdBy : userId,
          isActive: true
        }, { merge: true });
        
        console.log('✅ MERGED employees in Firestore');
        console.log('🔍 Merge completed at:', new Date().toISOString());
        console.log('🔍 Final employee count:', finalEmployees.length);
      }

      result.success = true;
      console.log(`✅ Import completed: ${result.successfulImports} successful, ${result.failedImports} failed`);
      
      // 🔍 DEBUG: Verify data was actually saved by reading it back
      console.log('🔍 VERIFICATION: Reading back from Firestore to confirm save...');
      const verifyDoc = await getDoc(projectDoc);
      if (verifyDoc.exists()) {
        const verifyData = verifyDoc.data();
        console.log('🔍 Firestore has', verifyData.employees?.length, 'employees after save');
        if (verifyData.employees && verifyData.employees.length > 0) {
          console.log('🔍 First employee from Firestore RIGHT AFTER SAVE:', verifyData.employees[0]);
          console.log('🔍 First employee projectCode RIGHT AFTER SAVE:', verifyData.employees[0].projectCode);
        }
      } else {
        console.error('❌ VERIFICATION FAILED: Document does not exist after save!');
      }
      
    } catch (error) {
      console.error('Import error:', error);
      result.success = false;
      result.errors.push(error instanceof Error ? error.message : 'Unknown error during import');
    }

    return result;
  }

  // ========================================================================
  // EXCEL EXPORT
  // ========================================================================
  
  /**
   * Export project employees to Excel format
   */
  async exportProjectToExcel(projectName: string): Promise<ExcelImportRow[]> {
    try {
      const project = await this.getProject(projectName);
      
      if (!project || !project.employees) {
        return [];
      }

      return project.employees.map((emp, index) => ({
        'S.NO': emp.sNo || (index + 1),
        'NAME': emp.name,
        'EMP. CODE': emp.empCode,
        'PROJECT CODE': emp.projectCode || '', // ✅ Ensure it's included
        'SITE': emp.site,
        'POSITION': emp.position
      }));
    } catch (error) {
      console.error('Error exporting project:', error);
      throw new Error('Failed to export project');
    }
  }

  /**
   * Generate Excel template
   */
  generateExcelTemplate(): ExcelImportRow[] {
    return [
      {
        'S.NO': 1,
        'NAME': 'Employee Name (auto-filled from MasterSheet)',
        'EMP. CODE': '2753',
        'SITE': 'Project Site Location',
        'POSITION': 'Job Position/Role',
        'PROJECT CODE': 'Project Code (Optional)'
      },
      {
        'S.NO': 2,
        'NAME': 'Will be filled automatically',
        'EMP. CODE': '2754',
        'SITE': 'Example Site',
        'POSITION': 'Foreman',
        'PROJECT CODE': 'PRJ-001'
      }
    ];
  }

  /**
   * Get analytics about employee assignments
   */
  async getEmployeeAssignmentAnalytics(): Promise<{
    totalInMasterSheet: number;
    totalAssigned: number;
    unassignedEmployees: EmployeeMasterData[];
    duplicateAssignments: {
      employee: EmployeeMasterData;
      projects: string[];
    }[];
  }> {
    try {
      console.log('📊 Calculating employee assignment analytics...');

      // Get all employees from MasterSheet
      const masterSheetEmployees = await this.getAllEmployeesFromMasterSheet();
      
      // Get all projects with employees
      const projects = await this.getAllProjects();
      
      // Create map of employee codes to project names
      const employeeToProjects = new Map<string, string[]>();
      
      projects.forEach(project => {
        project.employees.forEach(emp => {
          const empCode = emp.empNumber; // Using full code like EMP2753
          if (!employeeToProjects.has(empCode)) {
            employeeToProjects.set(empCode, []);
          }
          employeeToProjects.get(empCode)!.push(project.projectName);
        });
      });

      // Find unassigned employees (in MasterSheet but not in any project)
      const unassignedEmployees = masterSheetEmployees.filter((masterEmp: EmployeeMasterData) => {
        return !employeeToProjects.has(masterEmp.employeeNumber);
      });

      // Find duplicate assignments (employees in multiple projects)
      const duplicateAssignments: {
        employee: EmployeeMasterData;
        projects: string[];
      }[] = [];

      employeeToProjects.forEach((projectList, empCode) => {
        if (projectList.length > 1) {
          const masterData = masterSheetEmployees.find((e: EmployeeMasterData) => e.employeeNumber === empCode);
          if (masterData) {
            duplicateAssignments.push({
              employee: masterData,
              projects: projectList
            });
          }
        }
      });

      const totalAssigned = employeeToProjects.size;

      console.log(`📊 Analytics: ${masterSheetEmployees.length} total, ${totalAssigned} assigned, ${unassignedEmployees.length} unassigned, ${duplicateAssignments.length} duplicates`);

      return {
        totalInMasterSheet: masterSheetEmployees.length,
        totalAssigned,
        unassignedEmployees,
        duplicateAssignments
      };
    } catch (error) {
      console.error('Error calculating analytics:', error);
      throw new Error('Failed to calculate employee assignment analytics');
    }
  }

  // ========================================================================
  // STATISTICS & ANALYTICS
  // ========================================================================
  
  /**
   * Get manpower allocation statistics
   */
  async getManpowerStats(): Promise<{
    totalProjects: number;
    totalEmployees: number;
    activeProjects: number;
    employeesByProject: { projectName: string; count: number }[];
  }> {
    try {
      const projects = await this.getAllProjects();
      
      const employeesByProject = projects.map(p => ({
        projectName: p.projectName,
        count: p.totalEmployees
      }));

      return {
        totalProjects: projects.length,
        totalEmployees: projects.reduce((sum, p) => sum + p.totalEmployees, 0),
        activeProjects: projects.filter(p => p.isActive).length,
        employeesByProject
      };
    } catch (error) {
      console.error('Error calculating stats:', error);
      throw new Error('Failed to calculate statistics');
    }
  }

  /**
   * Search employees across all projects
   */
  async searchEmployee(empCode: string): Promise<{
    found: boolean;
    projects: string[];
    employeeData?: ManpowerEmployee;
  }> {
    try {
      const projects = await this.getAllProjects();
      const foundProjects: string[] = [];
      let employeeData: ManpowerEmployee | undefined;

      for (const project of projects) {
        const employee = project.employees.find(emp => 
          emp.empCode === empCode || 
          emp.empNumber === this.formatEmployeeNumber(empCode)
        );
        
        if (employee) {
          foundProjects.push(project.projectName);
          if (!employeeData) {
            employeeData = employee;
          }
        }
      }

      return {
        found: foundProjects.length > 0,
        projects: foundProjects,
        employeeData
      };
    } catch (error) {
      console.error('Error searching employee:', error);
      throw new Error('Failed to search employee');
    }
  }

  // ========================================================================
  // REAL-TIME LISTENER
  // ========================================================================
  
  /**
   * Listen to project changes in real-time
   */
  onProjectChanges(
    projectName: string,
    callback: (project: ManpowerProject) => void,
    onError?: (error: Error) => void
  ): Unsubscribe {
    const projectDoc = doc(db, this.COLLECTION_PATH, projectName);
    
    return onSnapshot(
      projectDoc,
      (snapshot) => {
        if (!snapshot.exists()) {
          onError?.(new Error('Project not found'));
          return;
        }

        const data = snapshot.data();
        const project: ManpowerProject = {
          id: snapshot.id,
          projectName: snapshot.id,
          projectCode: data.projectCode || '',
          location: data.location || '',
          totalEmployees: data.employees?.length || 0,
          employees: data.employees || [],
          createdAt: data.createdAt || Timestamp.now(),
          updatedAt: data.updatedAt || Timestamp.now(),
          createdBy: data.createdBy || 'Unknown',
          lastModifiedBy: data.lastModifiedBy || 'Unknown',
          isActive: data.isActive !== false
        };

        callback(project);
      },
      (error) => {
        onError?.(error);
      }
    );
  }

  /**
   * Listen to all projects changes
   */
  onAllProjectsChanges(
    callback: (projects: ManpowerProject[]) => void,
    onError?: (error: Error) => void
  ): Unsubscribe {
    const projectsRef = collection(db, this.COLLECTION_PATH);
    const q = query(projectsRef, orderBy('updatedAt', 'desc'));
    
    return onSnapshot(
      q,
      (snapshot) => {
        const projects: ManpowerProject[] = [];
        
        snapshot.forEach(doc => {
          const data = doc.data();
          projects.push({
            id: doc.id,
            projectName: doc.id,
            projectCode: data.projectCode || '',
            location: data.location || '',
            totalEmployees: data.employees?.length || 0,
            employees: data.employees || [],
            createdAt: data.createdAt || Timestamp.now(),
            updatedAt: data.updatedAt || Timestamp.now(),
            createdBy: data.createdBy || 'Unknown',
            lastModifiedBy: data.lastModifiedBy || 'Unknown',
            isActive: data.isActive !== false
          });
        });

        callback(projects);
      },
      (error) => {
        onError?.(error);
      }
    );
  }
}

// ============================================================================
// EXPORT SINGLETON INSTANCE
// ============================================================================

export const manpowerService = new ManpowerService();